import './Approvals.scss';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Card, Form } from 'react-bootstrap';
import { Button } from '@Components/Button/Button';
import { Dropdown } from '@Components/Dropdown';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { PendingCreativesData } from '@blisspointmedia/bpm-types/dist/PendingCreatives';
import { ApprovalStages, Reviewer } from '@blisspointmedia/bpm-types/dist/CreativeApprovals';
import { ClientStrategyContact } from '@blisspointmedia/bpm-types/dist/AccountPlanning';
import { BPMTable, TypedHeader } from '@Components/StickyTable/BPMTable';
import { FullPageSpinner } from '@Components/Spinner';
import { Skeleton, TableSkeleton } from '@Components/Skeleton';
import { useAppSelector } from '../../redux';
import { useCreativeMap } from '../../redux/creative';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { emailSelector } from '../../redux/user';
import { awaitJSON, MiscLambdaFetch, StreamingLambdaFetch } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { StreamingCreativesContext, TabKey } from './streaming-creatives-constants';

const renderNoRows = () => <div>No rows to show.</div>;

interface ProcessedRow {
  placementName: string;
  dateKey: string;
  isci: string;
  pct: number;
  start_date: string;
  end_date: string | null;
  has_active_or_future_flights: boolean;
  creativeName: string | null;
}

export const Approvals = () => {
  const { cid: company } = useCompanyInfo();
  const { creativeMap } = useCreativeMap({
    company,
    mediaTypes: ['streaming', 'audio', 'display']
  });

  const [reviewerOptions, setReviewerOptions] = useState<Reviewer[]>();

  const setAreYouSure = useSetAreYouSure();

  const [selectedReviewers, setSelectedReviewers] = useState<Reviewer[]>([]);

  const { tab, goToTab, approvalStageData, updateApprovalStage } =
    useContext(StreamingCreativesContext);

  const [approvalStage, setApprovalStage] = useState<ApprovalStages | null>(null);

  const setError = useSetError();

  const [commentBoxText, setCommentBoxText] = useState<string>();

  const [pendingCreatives, setPendingCreatives] = useState<PendingCreativesData>();

  const userEmail = useAppSelector(emailSelector);

  const [currentRequester, setCurrentRequester] = useState('');

  const [currentReviewer, setCurrentReviewer] = useState<string | null>(null);

  const [showCommentBox, setShowCommentBox] = useState(false);

  useEffect(() => {
    if (approvalStageData) {
      setApprovalStage(approvalStageData.stage || ApprovalStages.PRE_REVIEW);
      setCurrentRequester(approvalStageData.requester || userEmail);
      setSelectedReviewers(approvalStageData.reviewers || []);
      if (approvalStageData.reviewers) {
        for (const reviewer of approvalStageData.reviewers) {
          if (userEmail === reviewer.value) {
            setCurrentReviewer(reviewer.value);
          }
        }
      }
    }
  }, [approvalStageData, userEmail]);

  useEffect(() => {
    (async () => {
      if (company) {
        try {
          const res = await StreamingLambdaFetch('/getPendingCreatives', {
            params: { company }
          });
          const data = await awaitJSON<PendingCreativesData>(res);
          setPendingCreatives(data);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      }
    })();
  }, [company, setError, tab]);

  useEffect(() => {
    (async () => {
      try {
        const res = await MiscLambdaFetch('/getClientStrategyContacts');
        const contacts = await awaitJSON<ClientStrategyContact[]>(res);
        const options: Reviewer[] = contacts.map(contact => ({
          label: contact.full_name ? contact.full_name : contact.email,
          value: contact.email
        }));
        options.push({ label: 'Carling Sugarman', value: 'carling.sugarman@tinuiti.com' });
        setReviewerOptions(options.sort((a, b) => a.label.localeCompare(b.label)));
      } catch (e) {
        setError({ message: e.message, reportError: e });
      }
    })();
  }, [company, setError]);

  const sendReviewRequest = useCallback(() => {
    setApprovalStage(ApprovalStages.IN_REVIEW);
    updateApprovalStage(company, ApprovalStages.IN_REVIEW, selectedReviewers, currentRequester);
  }, [company, updateApprovalStage, selectedReviewers, currentRequester]);

  const requestChanges = useCallback(() => {
    setShowCommentBox(false);
    setApprovalStage(ApprovalStages.CHANGES_REQUESTED);
    updateApprovalStage(
      company,
      ApprovalStages.CHANGES_REQUESTED,
      selectedReviewers,
      currentRequester,
      currentReviewer,
      commentBoxText
    );
  }, [
    company,
    updateApprovalStage,
    selectedReviewers,
    currentRequester,
    currentReviewer,
    commentBoxText
  ]);

  const approveRotation = useCallback(() => {
    setApprovalStage(ApprovalStages.APPROVED);
    updateApprovalStage(
      company,
      ApprovalStages.APPROVED,
      selectedReviewers,
      currentRequester,
      currentReviewer,
      commentBoxText
    );
  }, [
    company,
    updateApprovalStage,
    selectedReviewers,
    currentRequester,
    currentReviewer,
    commentBoxText
  ]);

  const resetApprovalProcess = useCallback(() => {
    setAreYouSure({
      title: 'Cancel Review Process',
      message: `${currentRequester} initiated this review process. Are you sure you want to cancel it?`,
      okayText: 'Yes, Cancel',
      cancelText: "No, Don't Cancel",
      onOkay: () => {
        setApprovalStage(ApprovalStages.PRE_REVIEW);
        updateApprovalStage(
          company,
          ApprovalStages.PRE_REVIEW,
          selectedReviewers,
          currentRequester
        );
      }
    });
  }, [company, updateApprovalStage, currentRequester, selectedReviewers, setAreYouSure]);

  const [showActivePlacementsOnly, setShowActivePlacementsOnly] = useState(false);

  // Restructure pending placements data for use in BPMTable.
  const processedData = useMemo(() => {
    if (!pendingCreatives || !creativeMap) {
      return undefined;
    }
    let rows: ProcessedRow[] = [];
    for (const placementName of Object.keys(pendingCreatives)) {
      const placement = pendingCreatives[placementName];
      for (const dateKey of Object.keys(placement)) {
        for (const entry of placement[dateKey]) {
          const { isci, pct, start_date, end_date, has_active_or_future_flights } = entry;
          rows.push({
            placementName,
            dateKey,
            isci,
            pct,
            start_date: start_date || 'None',
            end_date: end_date || 'None',
            has_active_or_future_flights,
            creativeName: creativeMap[isci]?.name ?? null
          });
        }
      }
    }
    if (showActivePlacementsOnly) {
      rows = rows.filter(row => row.has_active_or_future_flights);
    }
    return rows;
  }, [pendingCreatives, creativeMap, showActivePlacementsOnly]);

  const headers: TypedHeader<ProcessedRow>[] = [
    { label: 'Placement Name', name: 'placementName', flex: 2 },
    { label: 'Creative Name', name: 'creativeName', flex: 1 },
    { label: 'ISCI', name: 'isci', flex: 1 },
    { label: 'Percent', name: 'pct', flex: 1 },
    { label: 'Start Date', name: 'start_date', flex: 1 },
    { label: 'End Date', name: 'end_date', flex: 1 }
  ];

  return (
    <div className='streaming-creatives-card-container'>
      {approvalStage ? (
        <Card className='streaming-creatives-card'>
          <div className='approvals'>
            {processedData ? (
              <div className='approvals-data-container'>
                <div className='active-placements-only-checkbox-container'>
                  <Checkbox
                    className='active-placements-only-checkbox'
                    id='activePlacementsOnly'
                    checked={showActivePlacementsOnly}
                    onChange={() => setShowActivePlacementsOnly(!showActivePlacementsOnly)}
                    label='Active placements only'
                  />
                </div>
                <BPMTable headers={headers} data={processedData} noRowsRenderer={renderNoRows} />
              </div>
            ) : (
              <Skeleton>
                <TableSkeleton />
              </Skeleton>
            )}
          </div>
          <div className='buttons-container'>
            {approvalStage !== ApprovalStages.PRE_REVIEW && (
              <div className='bottom-page-container'>
                <Button
                  size={Size.LG}
                  appearance={Appearance.OUTLINED}
                  design={Design.NEUTRAL}
                  onClick={() => goToTab(TabKey.CART)}
                >
                  Back
                </Button>
                <Button
                  size={Size.LG}
                  className='cancel-button'
                  appearance={Appearance.EMPTY}
                  onClick={resetApprovalProcess}
                >
                  Cancel Review
                </Button>
                {!showCommentBox && approvalStage === ApprovalStages.IN_REVIEW && (
                  <Button
                    size={Size.LG}
                    className='display-reviewers'
                    design={Design.NEUTRAL}
                    disabled
                  >
                    {currentReviewer
                      ? `Review requested by ${currentRequester}.`
                      : `Review has been requested from ${selectedReviewers.map(reviewer =>
                          ' '.concat(reviewer.label)
                        )}.`}
                  </Button>
                )}
                {approvalStage === ApprovalStages.APPROVED && (
                  <div className='approved'>
                    <Button
                      size={Size.LG}
                      className='display-reviewers'
                      appearance={Appearance.OUTLINED}
                      disabled
                    >
                      {`Changes have been approved by ${approvalStageData?.currentReviewer}.`}
                    </Button>
                    <Button
                      size={Size.LG}
                      className='next-button'
                      onClick={() => goToTab(TabKey.GENERATE)}
                    >
                      Next
                    </Button>
                  </div>
                )}
                {approvalStage === ApprovalStages.CHANGES_REQUESTED && (
                  <div className='approved'>
                    <Button
                      size={Size.LG}
                      className='display-reviewers'
                      appearance={Appearance.OUTLINED}
                      design={Intent.ERROR}
                      disabled
                    >
                      {`Changes have been requested by ${currentReviewer}.`}
                    </Button>
                    {userEmail === currentRequester && (
                      <Button
                        size={Size.LG}
                        className='rerequest-review-button'
                        onClick={sendReviewRequest}
                      >
                        Re-Request Review
                      </Button>
                    )}
                  </div>
                )}
                {approvalStage === ApprovalStages.IN_REVIEW &&
                  currentReviewer &&
                  !showCommentBox && (
                    <div className='comment-box-and-buttons-container'>
                      <Button size={Size.LG} className='approve-button' onClick={approveRotation}>
                        Approve
                      </Button>
                      <Button
                        size={Size.LG}
                        className='request-changes-button'
                        design={Intent.ERROR}
                        onClick={() => setShowCommentBox(true)}
                      >
                        Request Changes
                      </Button>
                    </div>
                  )}
                {showCommentBox && (
                  <div className='comment-box-container'>
                    <Form.Control
                      className='comment-box'
                      as='textarea'
                      rows={1}
                      placeholder='Changes Requested...'
                      value={commentBoxText || ''}
                      onChange={e => setCommentBoxText(e.target.value)}
                      onBlur={() => setCommentBoxText(text => (text || '').trim())}
                    />
                    <Button
                      size={Size.LG}
                      className='confirm-request-changes-button'
                      design={Intent.ERROR}
                      onClick={requestChanges}
                    >
                      Request Changes
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          {approvalStage === ApprovalStages.PRE_REVIEW && (
            <div className='review-controls'>
              <Button
                size={Size.LG}
                appearance={Appearance.OUTLINED}
                design={Design.NEUTRAL}
                onClick={() => goToTab(TabKey.CART)}
              >
                Back
              </Button>
              <Dropdown
                size={Size.LG}
                className='reviewer-options-menu'
                multiSelect
                searchable
                showChips
                placeholder='Select Reviewers...'
                menuPortalTarget={document.body}
                value={selectedReviewers.map(reviewer => reviewer.value)}
                options={reviewerOptions}
                onChange={values =>
                  setSelectedReviewers(
                    (reviewerOptions ?? []).filter(option => values.includes(option.value))
                  )
                }
              />
              <Button
                size={Size.LG}
                disabled={!selectedReviewers?.length}
                onClick={sendReviewRequest}
              >
                Request Review
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <FullPageSpinner />
      )}
    </div>
  );
};
